import { useState } from 'react'
import {
  User, Phone, Mail, MapPin, Calendar, Briefcase, Building2,
  FileText, ShieldCheck, Activity,
} from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { BelgelerModal } from '@/components/belge/BelgelerModal'
import clsx from 'clsx'
import type { Vardiyali, Sube } from '@/types'

interface Props {
  acik: boolean
  personel: Vardiyali | null
  pozisyonHaritasi: Map<string, string>
  subeHaritasi: Map<string, Sube>
  bugunkuDurum: 'mesaide' | 'yok'
  onKapat: () => void
}

export function YoneticiPersonelDetayModal({
  acik, personel, pozisyonHaritasi, subeHaritasi, bugunkuDurum, onKapat,
}: Props) {
  const [belgelerAcik, setBelgelerAcik] = useState(false)

  if (!personel) return null

  const pozisyonAdlari = (personel.pozisyonIdleri ?? [])
    .map(id => pozisyonHaritasi.get(id))
    .filter(Boolean)
    .join(', ') || '—'
  const subeAdlari = (personel.subeIdleri ?? [])
    .map(id => subeHaritasi.get(id)?.isim)
    .filter(Boolean)
    .join(', ') || '—'

  return (
    <>
      <Modal acik={acik} onKapat={onKapat} baslik="Personel Detayı" boyut="lg">
        <div className="space-y-4">
          {/* Üst — isim + durum */}
          <div className="flex items-start gap-3 pb-3 border-b border-gray-100">
            {(personel as Vardiyali & { profilFotoUrl?: string }).profilFotoUrl ? (
              <img
                src={(personel as Vardiyali & { profilFotoUrl?: string }).profilFotoUrl}
                alt={personel.isim}
                className="w-14 h-14 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xl font-bold flex-shrink-0">
                {personel.isim.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-gray-900">{personel.isim}</p>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={clsx(
                    'text-[10px] px-2 py-0.5 rounded-full font-bold uppercase',
                    bugunkuDurum === 'mesaide'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  )}
                >
                  <Activity className="w-3 h-3 inline mr-0.5" />
                  {bugunkuDurum === 'mesaide' ? 'Mesaide' : 'Yok'}
                </span>
                {personel.yoneticiMi && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold uppercase">
                    <ShieldCheck className="w-3 h-3 inline mr-0.5" />
                    Yönetici
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Bilgi alanları */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Alan ikon={Briefcase} baslik="Pozisyon" deger={pozisyonAdlari} />
            <Alan ikon={Building2} baslik="Şube" deger={subeAdlari} />
            {personel.telefon && (
              <Alan ikon={Phone} baslik="Telefon" deger={personel.telefon} />
            )}
            {personel.email && (
              <Alan ikon={Mail} baslik="E-posta" deger={personel.email} />
            )}
            {personel.iseBaslamaTarihi && (
              <Alan
                ikon={Calendar}
                baslik="İşe Başlama"
                deger={new Date(personel.iseBaslamaTarihi).toLocaleDateString('tr-TR')}
              />
            )}
            {personel.dogumTarihi && (
              <Alan
                ikon={Calendar}
                baslik="Doğum Tarihi"
                deger={new Date(personel.dogumTarihi).toLocaleDateString('tr-TR')}
              />
            )}
            {personel.adres && (
              <Alan ikon={MapPin} baslik="Adres" deger={personel.adres} />
            )}
            <Alan
              ikon={User}
              baslik="Çalışma Tipi"
              deger={personel.calismaTipi === 'fulltime' ? 'Tam zamanlı' : 'Yarı zamanlı'}
            />
          </div>

          {/* Acil iletişim */}
          {personel.acilIletisim?.isim && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-900 uppercase mb-1">
                Acil İletişim
              </p>
              <p className="text-sm text-amber-900">
                {personel.acilIletisim.isim}
                {personel.acilIletisim.telefon && ` · ${personel.acilIletisim.telefon}`}
              </p>
            </div>
          )}

          {/* Belgeler butonu */}
          <button
            onClick={() => setBelgelerAcik(true)}
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Belgeleri Görüntüle
          </button>

          <p className="text-xs text-gray-500 text-center">
            Maaş, IBAN ve özlük bilgileri yetkisi BOSS'tadır.
          </p>
        </div>
      </Modal>

      <BelgelerModal
        acik={belgelerAcik}
        personel={personel}
        onKapat={() => setBelgelerAcik(false)}
      />
    </>
  )
}

function Alan({
  ikon: Icon, baslik, deger,
}: {
  ikon: typeof User
  baslik: string
  deger: string
}) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <p className="text-[10px] font-semibold text-gray-500 uppercase flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" /> {baslik}
      </p>
      <p className="text-sm font-medium text-gray-900 break-words">{deger}</p>
    </div>
  )
}
